import { cached, tracked } from "@glimmer/tracking";
import EmberObject, { computed, get, set } from "@ember/object";
import { dependentKeyCompat } from "@ember/object/compat";
import { service } from "@ember/service";
import { isEmpty } from "@ember/utils";
import { Promise } from "rsvp";
import { resolveShareUrl } from "discourse/helpers/share-url";
import { ajax } from "discourse/lib/ajax";
import { popupAjaxError } from "discourse/lib/ajax-error";
import {
  clearModelFields,
  modelFieldNames,
  registerModelField,
  stampModelClass,
} from "discourse/lib/model-extensions";
import { deepEqual } from "discourse/lib/object";
import { cook } from "discourse/lib/text";
import { fancyTitle } from "discourse/lib/topic-fancy-title";
import { enumerateTrackedKeys } from "discourse/lib/tracked-tools";
import { applyValueTransformer } from "discourse/lib/transformer";
import { userPath } from "discourse/lib/url";
import { postUrl } from "discourse/lib/utilities";
import ActionSummary from "discourse/models/action-summary";
import Badge from "discourse/models/badge";
import Composer from "discourse/models/composer";
import RestModel from "discourse/models/rest";
import Site from "discourse/models/site";
import User from "discourse/models/user";
import { i18n } from "discourse-i18n";

/**
 * @internal
 * Adds a tracked property to the post model.
 *
 * Intended to be used only in the plugin API.
 *
 * @param {string} propertyKey - The key of the property to track.
 */
export function _addTrackedPostProperty(propertyKey) {
  stampModelClass(Post, "post");
  registerModelField("post", propertyKey);
}

/**
 * Clears all tracked properties added using the API
 *
 * USE ONLY FOR TESTING PURPOSES.
 */
export function clearAddedTrackedPostProperties() {
  clearModelFields("post");
}

export default class Post extends RestModel {
  static munge(json) {
    json.likeAction ??= null;
    if (json.actions_summary) {
      const lookup = EmberObject.create();

      // this area should be optimized, it is creating way too many objects per post
      json.actions_summary = json.actions_summary.map((a) => {
        a.actionType = Site.current().postActionTypeById(a.id);
        a.count = a.count || 0;
        const actionSummary = ActionSummary.create(a);
        lookup[a.actionType.name_key] = actionSummary;

        if (a.actionType.name_key === "like") {
          json.likeAction = actionSummary;
        }
        return actionSummary;
      });

      json.actionByName = lookup;
    }

    if (json && json.reply_to_user) {
      json.reply_to_user = User.create(json.reply_to_user);
    }

    return json;
  }

  static updateBookmark(postId, bookmarked) {
    return ajax(`/posts/${postId}/bookmark`, {
      type: "PUT",
      data: { bookmarked },
    });
  }

  static destroyBookmark(postId) {
    return ajax(`/posts/${postId}/bookmark`, {
      type: "DELETE",
    });
  }

  static deleteMany(post_ids, { agreeWithFirstReplyFlag = true } = {}) {
    return ajax("/posts/destroy_many", {
      type: "DELETE",
      data: { post_ids, agree_with_first_reply_flag: agreeWithFirstReplyFlag },
    });
  }

  static mergePosts(post_ids) {
    return ajax("/posts/merge_posts", {
      type: "PUT",
      data: { post_ids },
    }).catch(popupAjaxError);
  }

  static loadRevision(postId, version) {
    return ajax(`/posts/${postId}/revisions/${version}.json`).then((result) => {
      result.categories?.forEach((c) => Site.current().updateCategory(c));
      return EmberObject.create(result);
    });
  }

  static hideRevision(postId, version) {
    return ajax(`/posts/${postId}/revisions/${version}/hide`, {
      type: "PUT",
    });
  }

  static permanentlyDeleteRevisions(postId) {
    return ajax(`/posts/${postId}/revisions/permanently_delete`, {
      type: "DELETE",
    });
  }

  static showRevision(postId, version) {
    return ajax(`/posts/${postId}/revisions/${version}/show`, {
      type: "PUT",
    });
  }

  static loadRawEmail(postId) {
    return ajax(`/posts/${postId}/raw-email.json`);
  }

  @service currentUser;
  @service site;

  @tracked action_code;
  @tracked action_code_path;
  @tracked action_code_who;
  @tracked actions_summary;
  @tracked admin;
  @tracked badges_granted;
  @tracked bookmarked;
  @tracked can_delete;
  @tracked can_permanently_delete;
  @tracked can_recover;
  @tracked can_see_hidden_post;
  @tracked can_view_edit_history;
  @tracked cooked;
  @tracked cooked_hidden;
  @tracked created_at;
  @tracked customShare = null;
  @tracked deleted_at;
  @tracked deleted_by;
  @tracked deleted_post_placeholder;
  @tracked excerpt;
  @tracked expandedExcerpt;
  @tracked group_moderator;
  @tracked hasGap;
  @tracked hidden;
  @tracked id;
  @tracked is_auto_generated;
  @tracked is_localized;
  @tracked language;
  @tracked last_wiki_edit;
  @tracked likeAction;
  @tracked link_counts;
  @tracked localization_outdated;
  @tracked localizedCooked;
  @tracked localized_oneboxes;
  @tracked locked;
  @tracked moderator;
  @tracked name;
  @tracked notice;
  @tracked notice_created_by_user;
  @tracked post_localizations;
  @tracked post_number;
  @tracked post_type;
  @tracked primary_group_name;
  @tracked quoted;
  @tracked read;
  @tracked readers_count;
  @tracked reply_count;
  @tracked reply_to_user;
  @tracked staff;
  @tracked staged;
  @tracked title_is_group;
  @tracked topic;
  @tracked topic_id;
  @tracked trust_level;
  @tracked updated_at;
  @tracked user_custom_fields;
  @tracked user_deleted;
  @tracked user_id;
  @tracked user_locale;
  @tracked user_suspended;
  @tracked user_title;
  @tracked username;
  @tracked version;
  @tracked via_email;
  @tracked wiki;
  @tracked yours;
  @tracked _can_edit;
  // for compatibility with existing code
  // mark fist post as deleted if topic was deleted
  // post is either highlighted as deleted or hidden/removed from the post stream
  // post or content still can be recovered

  @dependentKeyCompat
  get can_edit() {
    return applyValueTransformer("post-can-edit", this._can_edit, {
      post: this,
    });
  }

  set can_edit(value) {
    this._can_edit = value;
  }

  @dependentKeyCompat
  get canEdit() {
    return this.can_edit;
  }

  set canEdit(value) {
    this.can_edit = value;
  }

  @computed("topic.details.created_by.id")
  get topicCreatedById() {
    return this.topic?.details?.created_by?.id;
  }

  set topicCreatedById(value) {
    set(this, "topic.details.created_by.id", value);
  }

  @dependentKeyCompat
  get new_user() {
    return this.trust_level === 0;
  }

  @dependentKeyCompat
  get firstPost() {
    return this.post_number === 1;
  }

  @computed("firstPost", "topic.deleted_at")
  get deletedViaTopic() {
    return this.firstPost && this.topic?.deleted_at;
  }

  @computed("deleted_at", "deletedViaTopic")
  get deleted() {
    return this.deleted_at || this.deletedViaTopic;
  }

  @computed("deleted")
  get notDeleted() {
    return !this.deleted;
  }

  @dependentKeyCompat
  get recoverable() {
    return this.deleted_at || this.user_deleted;
  }

  @computed("topic.details.created_by.id", "user_id")
  get topicOwner() {
    return deepEqual(this.topic?.details?.created_by?.id, this.user_id);
  }

  get shareUrl() {
    return this.customShare || resolveShareUrl(this.url, this.currentUser);
  }

  @computed("name", "username")
  get showName() {
    return (
      this.name &&
      this.name !== this.username &&
      this.siteSettings.display_name_on_posts
    );
  }

  get deletedBy() {
    return this.firstPost ? this.topic?.deleted_by : this.deleted_by;
  }

  get deletedAt() {
    return this.firstPost ? this.topic?.deleted_at : this.deleted_at;
  }

  @computed("post_number", "topic_id", "topic.slug")
  get url() {
    return postUrl(
      this.topic?.slug || this.topic_slug,
      this.topic_id || this.get("topic.id"),
      this.post_number
    );
  }

  // Don't drop the /1
  @computed("post_number", "url")
  get urlWithNumber() {
    return this.post_number === 1 ? `${this.url}/1` : this.url;
  }

  @computed("username")
  get usernameUrl() {
    return userPath(this.username);
  }

  get internalLinks() {
    if (isEmpty(this.link_counts)) {
      return null;
    }

    return this.link_counts.filter((link) => link.internal && link.title);
  }

  @computed("actions_summary.@each.can_act")
  get flagsAvailable() {
    // TODO: Investigate why `this.site` is sometimes null when running
    // Search - Search with context
    if (!this.site) {
      return [];
    }

    return this.site.flagTypes.filter((item) =>
      this.get(`actionByName.${item.name_key}.can_act`)
    );
  }

  @computed("siteSettings.use_pg_headlines_for_excerpt", "topic_title_headline")
  get useTopicTitleHeadline() {
    return (
      this.siteSettings?.use_pg_headlines_for_excerpt &&
      this.topic_title_headline
    );
  }

  @computed("topic_title_headline")
  get topicTitleHeadline() {
    return fancyTitle(
      this.topic_title_headline,
      this.siteSettings.support_mixed_text_direction
    );
  }

  get canBookmark() {
    return !!this.currentUser;
  }

  get canDelete() {
    return (
      this.can_delete &&
      !this.deleted_at &&
      this.currentUser &&
      (this.currentUser.staff || !this.user_deleted)
    );
  }

  get canDeleteTopic() {
    return this.firstPost && !this.deleted && this.topic.details.can_delete;
  }

  get canEditStaffNotes() {
    return !!this.topic.details.can_edit_staff_notes;
  }

  get canFlag() {
    return !this.topic.deleted && !isEmpty(this.flagsAvailable);
  }

  get canManage() {
    return this.currentUser?.canManageTopic;
  }

  get canPermanentlyDelete() {
    return (
      this.deleted &&
      (this.firstPost
        ? this.topic.details.can_permanently_delete
        : this.can_permanently_delete)
    );
  }

  get canPublishPage() {
    return this.firstPost && !!this.topic.details.can_publish_page;
  }

  get canRecoverTopic() {
    return (
      this.firstPost &&
      (this.deleted || this.user_deleted) &&
      this.topic.details.can_recover
    );
  }

  get isRecoveringTopic() {
    return (
      this.firstPost &&
      !this.deleted &&
      !this.user_deleted &&
      this.topic.details.can_recover
    );
  }

  get canRecover() {
    return !this.canRecoverTopic && this.recoverable && this.can_recover;
  }

  get isRecovering() {
    return !this.isRecoveringTopic && !this.recoverable && this.can_recover;
  }

  get canSplitMergeTopic() {
    return !!this.topic?.details?.can_split_merge_topic;
  }

  get canToggleLike() {
    return !!this.likeAction?.get("canToggle");
  }

  get filteredRepliesPostNumber() {
    return this.topic.get("postStream.filterRepliesToPostNumber");
  }

  get hasReplies() {
    return this.reply_count > 0;
  }

  get isWhisper() {
    return this.post_type === this.site.post_types.whisper;
  }

  get isModeratorAction() {
    return this.post_type === this.site.post_types.moderator_action;
  }

  get isWarning() {
    return this.topic?.is_warning;
  }

  get isSmallAction() {
    return applyValueTransformer(
      "post-is-small-action",
      this.post_type === this.site.post_types.small_action ||
        this.action_code === "split_topic",
      { post: this }
    );
  }

  get liked() {
    return !!this.likeAction?.get("acted");
  }

  get likeCount() {
    return this.likeAction?.get("count");
  }

  /**
   * Show a "Flag to delete" message if not staff and you can't otherwise delete it.
   */
  get showFlagDelete() {
    return (
      !this.canDelete &&
      this.yours &&
      this.canFlag &&
      this.currentUser &&
      !this.currentUser.staff
    );
  }

  get showLike() {
    if (
      !this.currentUser ||
      (this.topic?.get("archived") && this.user_id !== this.currentUser.id)
    ) {
      return true;
    }

    return this.likeAction && (this.liked || this.canToggleLike);
  }

  @cached
  get user() {
    if (!this.user_id || !this.username) {
      // If we don't have at least user_id and username, we can't create a User instance.
      return null;
    }

    // Using store.createRecord can lead to issues when updating existing models in the cache, potentially causing
    // rendering errors if the cached model is currently being rendered.
    // Instead, User.create ensures we get a fresh instance every time without affecting the cache.
    // The @cached decorator ensures this computation only happens once and is cached until dependencies are updated.
    return User.create({
      id: this.user_id,
      username: this.username,
      name: this.name,
      admin: this.admin,
      avatar_template: this.avatar_template,
      flair_bg_color: this.flair_bg_color,
      flair_color: this.flair_color,
      flair_group_id: this.flair_group_id,
      flair_name: this.flair_name,
      flair_url: this.flair_url,
      locale: this.user_locale,
      moderator: this.moderator,
      primary_group_name: this.primary_group_name,
      status: this.user_status,
      title: this.user_title,
      trust_level: this.trust_level,
      custom_fields: this.user_custom_fields,
    });
  }

  get topicNotificationLevel() {
    return this.topic.details.notification_level;
  }

  get userBadges() {
    if (!this.topic?.user_badges) {
      return;
    }
    const badgeIds = this.topic.user_badges.users[this.user_id]?.badge_ids;
    if (badgeIds) {
      return badgeIds.map((badgeId) => this.topic.user_badges.badges[badgeId]);
    }
  }

  @cached
  get badgesGranted() {
    return this.badges_granted?.map((json) => {
      const badges = Badge.createFromJson(json);
      return Array.isArray(badges) ? badges[0] : badges;
    });
  }

  get requestedGroupName() {
    return this.post_number === 1 ? this.topic?.requested_group_name : null;
  }

  get expandablePost() {
    return this.post_number === 1 && !!this.topic?.expandable_first_post;
  }

  get topicUrl() {
    return this.topic?.url;
  }

  @cached
  get actionsSummary() {
    return this.actions_summary
      ?.filter((postAction) => {
        return postAction.actionType.name_key !== "like" && postAction.acted;
      })
      ?.map((postAction) => {
        return {
          id: postAction.id,
          postId: this.id,
          action: postAction.actionType.name_key,
          canUndo: postAction.can_undo,
          description: postAction.actionType.translatedDescription,
        };
      });
  }

  get displayDate() {
    if (this.wiki && this.last_wiki_edit) {
      return this.last_wiki_edit;
    } else {
      return this.created_at;
    }
  }

  updatePostField(field, value) {
    const data = {};
    data[field] = value;

    return ajax(`/posts/${this.id}/${field}`, { type: "PUT", data })
      .then((response) => {
        this.set(field, value);
        return response;
      })
      .catch(popupAjaxError);
  }

  afterUpdate(res) {
    if (res.category) {
      this.site.updateCategory(res.category);
    }
  }

  updateProperties() {
    return {
      post: { raw: this.raw, edit_reason: this.editReason },
      image_sizes: this.imageSizes,
    };
  }

  createProperties() {
    // composer only used once, defer the dependency
    const data = this.getProperties(Composer.serializedFieldsForCreate());
    data.reply_to_post_number = this.reply_to_post_number;
    data.image_sizes = this.imageSizes;

    const metaData = this.metaData;

    // Put the metaData into the request
    if (metaData) {
      data.meta_data = {};
      Object.keys(metaData).forEach(
        (key) => (data.meta_data[key] = metaData[key])
      );
    }

    return data;
  }

  // Expands the first post's content, if embedded and shortened.
  async expand() {
    try {
      const post = await ajax(`/posts/${this.id}/expand-embed`);
      this.cooked = `<section class="expanded-embed">${post.cooked}</section>`;
    } catch (error) {
      popupAjaxError.call(this, error);
      throw error;
    }
  }

  // Recover a deleted post
  recover() {
    const initProperties = this.getProperties(
      "deleted_at",
      "deleted_by",
      "user_deleted",
      "can_delete"
    );

    this.setProperties({
      deleted_at: null,
      deleted_by: null,
      user_deleted: false,
      can_delete: false,
    });

    return ajax(`/posts/${this.id}/recover`, {
      type: "PUT",
    })
      .then((data) => {
        this.setProperties({
          cooked: data.cooked,
          raw: data.raw,
          user_deleted: false,
          can_delete: true,
          version: data.version,
        });
      })
      .catch((error) => {
        popupAjaxError(error);
        this.setProperties(initProperties);
      });
  }

  /**
   Changes the state of the post to be deleted. Does not call the server, that should be
   done elsewhere.
   **/
  setDeletedState(deletedBy) {
    let promise;
    this.set("oldCooked", this.cooked);
    this.set("oldCanEdit", this.can_edit);

    // Moderators can delete posts. Users can only trigger a deleted at message, unless delete_removed_posts_after is 0.
    if (deletedBy.staff || this.siteSettings.delete_removed_posts_after === 0) {
      this.setProperties({
        deleted_at: new Date(),
        deleted_by: deletedBy,
        can_delete: false,
        can_permanently_delete:
          this.siteSettings.can_permanently_delete && deletedBy.admin,
        can_recover: true,
      });
    } else {
      const key =
        this.post_number === 1
          ? "topic.deleted_by_author_simple"
          : "post.deleted_by_author_simple";
      promise = cook(i18n(key)).then((cooked) => {
        this.setProperties({
          cooked,
          can_delete: false,
          can_permanently_delete: false,
          version: this.version + 1,
          can_recover: true,
          can_edit: false,
          user_deleted: true,
        });
      });
    }

    return promise || Promise.resolve();
  }

  /**
   Changes the state of the post to NOT be deleted. Does not call the server.
   This can only be called after setDeletedState was called, but the delete
   failed on the server.
   **/
  undoDeleteState({ force_destroy = false } = {}) {
    if (force_destroy || !this.oldCooked) {
      return;
    }

    this.setProperties({
      deleted_at: null,
      deleted_by: null,
      cooked: this.oldCooked,
      version: this.version - 1,
      can_recover: false,
      can_delete: true,
      user_deleted: false,
      can_edit: this.oldCanEdit ?? false,
    });
  }

  destroy(deletedBy, opts) {
    const maybeSetDeletedState = opts?.force_destroy
      ? Promise.resolve()
      : this.setDeletedState(deletedBy);

    return maybeSetDeletedState.then(() => {
      return ajax(`/posts/${this.id}`, {
        data: { context: window.location.pathname, ...opts },
        type: "DELETE",
      });
    });
  }

  /**
   * Updates a post from another's attributes. This will normally happen when a post is loading but
   * is already found in an identity map.
   **/
  updateFromPost(otherPost) {
    [
      ...Object.keys(otherPost),
      ...enumerateTrackedKeys(otherPost),
      ...modelFieldNames("post"),
    ].forEach((key) => {
      let value = otherPost[key],
        oldValue = this[key];

      if (!value) {
        value = null;
      }
      if (!oldValue) {
        oldValue = null;
      }

      let skip = false;
      if (typeof value !== "function" && oldValue !== value) {
        // wishing for an identity map
        if (key === "reply_to_user" && value && oldValue) {
          skip =
            value.username === oldValue.username ||
            get(value, "username") === get(oldValue, "username");
        } else if (key === "topic" && !value && oldValue) {
          // if `topic` is already set in the new instance we don't want to overwrite it with null, because the old
          // instance wasn't fully initialized yet.
          skip = true;
        }

        if (!skip) {
          this.set(key, value);
        }
      }
    });
  }

  expandHidden() {
    return ajax(`/posts/${this.id}/cooked.json`).then((result) => {
      this.setProperties({ cooked: result.cooked, cooked_hidden: false });
    });
  }

  async toggleLocalizedContent() {
    if (this.localizedCooked) {
      this.setProperties({
        cooked: this.localizedCooked,
        localizedCooked: null,
      });
    } else {
      const result = await ajax(`/posts/${this.id}/cooked.json`);

      this.setProperties({
        localizedCooked: this.cooked,
        cooked: result.cooked,
      });
    }
  }

  rebake() {
    return ajax(`/posts/${this.id}/rebake`, { type: "PUT" }).catch(
      popupAjaxError
    );
  }

  unhide() {
    return ajax(`/posts/${this.id}/unhide`, { type: "PUT" });
  }

  createBookmark(data) {
    this.setProperties({
      "topic.bookmarked": true,
      bookmarked: true,
      bookmark_reminder_at: data.reminder_at,
      bookmark_auto_delete_preference: data.auto_delete_preference,
      bookmark_name: data.name,
      bookmark_id: data.id,
    });
    this.topic.incrementProperty("bookmarksWereChanged");
    this.appEvents.trigger("bookmarks:changed", data, {
      target: "post",
      targetId: this.id,
    });
  }

  deleteBookmark(bookmarked) {
    this.set("topic.bookmarked", bookmarked);
    this.clearBookmark();
  }

  clearBookmark() {
    this.setProperties({
      bookmark_reminder_at: null,
      bookmark_name: null,
      bookmark_id: null,
      bookmarked: false,
      bookmark_auto_delete_preference: null,
    });
    this.topic.incrementProperty("bookmarksWereChanged");
    this.appEvents.trigger("bookmarks:changed", null, {
      target: "post",
      targetId: this.id,
    });
  }

  updateActionsSummary(json) {
    if (json && json.id === this.id) {
      json = Post.munge(json);
      this.set("actions_summary", json.actions_summary);
    }
  }

  updateLikeCount(count, userId, eventType) {
    let ownAction = this.currentUser?.id === userId;
    let ownLike = ownAction && eventType === "liked";
    let current_actions_summary = this.get("actions_summary");
    let likeActionID = Site.current().post_action_types.find(
      (a) => a.name_key === "like"
    ).id;
    const newActionObject = { id: likeActionID, count, acted: ownLike };

    if (!this.actions_summary.find((entry) => entry.id === likeActionID)) {
      let json = Post.munge({
        id: this.id,
        actions_summary: [newActionObject],
      });
      this.set(
        "actions_summary",
        Object.assign(current_actions_summary, json.actions_summary)
      );
      this.set("actionByName", json.actionByName);
      this.set("likeAction", json.likeAction);
    } else {
      newActionObject.acted =
        (ownLike || this.likeAction.acted) &&
        !(eventType === "unliked" && ownAction);

      Object.assign(
        this.actions_summary.find((entry) => entry.id === likeActionID),
        newActionObject
      );
      Object.assign(this.actionByName["like"], newActionObject);
      Object.assign(this.likeAction, newActionObject);
    }
  }

  revertToRevision(version) {
    return ajax(`/posts/${this.id}/revisions/${version}/revert`, {
      type: "PUT",
    });
  }
}
